import { useCallback, useMemo } from 'react';
import { generateStandardFilename } from '@fortress-technology-solutions/fortress-component-library/utils';
import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { exportAndDownloadPDF } from '../../utils/pdf-helper';
import v1Messages from '../ManageBuildings/messages';

const EXCLUDED_HEADERS = ['actions'];
const FILE_TYPE = 'manage-buildings';

/**
 * The library CSV builder blanks every falsy cell, so a count of 0 is exported as its string
 * form. Screen placeholders ('---') leave the files as empty cells.
 */
const toExportRow = (row: Object) =>
  Object.fromEntries(
    Object.entries(row).map(([key, cell]) => {
      if (key === 'id') return [key, cell];
      if (cell.value === DASHES) return [key, { ...cell, value: '' }];
      if (key === 'unitsCount') {
        return [key, { ...cell, value: String(cell.value) }];
      }
      return [key, cell];
    }),
  );

const useExport = ({
  filteredHeaders,
  rows,
  hasAnyFilters,
  propertyName,
  intl,
}: {
  filteredHeaders: Object[],
  rows: Object[],
  hasAnyFilters: boolean,
  propertyName: ?string,
  intl: Object,
}) => {
  const exportRows = useMemo(() => rows.map(toExportRow), [rows]);

  const onCSVButtonClick = useCallback(() => {
    exportAndDownloadCSV({
      excludedHeaders: EXCLUDED_HEADERS,
      filteredHeaders,
      fileName: generateStandardFilename({
        type: FILE_TYPE,
        extension: 'csv',
      }),
      rows: exportRows,
      hasAnyFilters,
    });
  }, [filteredHeaders, exportRows, hasAnyFilters]);

  const onPDFButtonClick = useCallback(() => {
    exportAndDownloadPDF({
      excludedHeaders: EXCLUDED_HEADERS,
      filteredHeaders,
      fileName: generateStandardFilename({
        type: FILE_TYPE,
        extension: 'pdf',
      }),
      rows: exportRows,
      hasAnyFilters,
      title: intl.formatMessage(v1Messages.header),
      subtitle: propertyName ?? '',
    });
  }, [filteredHeaders, exportRows, hasAnyFilters, propertyName, intl]);

  return { onCSVButtonClick, onPDFButtonClick };
};

export default useExport;
